import React from "react";
import { Link } from "react-router-dom";

import useAuth from "hooks/useAuth";
import { useBusiness } from "@hooks/useBusiness";
import { useStorePath } from "@hooks/useStorePath";

// Stocko wordmark. Was stocko-blue-bag-black-word.svg, which is no longer in
// the repo - this is the closest remaining asset.
import logo from "@/assets/img/layout/stocko_black_logo_dashboard.svg";

// third-party icons (react-icons)
import {
  IoSearchOutline,
  IoNotificationsOutline,
  IoChevronDown,
  IoSparkles,
  IoMenuOutline,
} from "react-icons/io5";

// A Wix-style global top bar: brand, account + nav links, centered search,
// and a right-hand tray of utility icons, account avatar and an AI button.
// Collapses progressively on smaller viewports.
const NavLink = ({ children, hasCaret = false }) => (
  <button className="flex items-center gap-1 rounded-md px-2 py-0.5 text-[13.5px] font-medium text-[#333] hover:bg-[#f4f4f6] transition-colors whitespace-nowrap">
    {children}
    {hasCaret && <IoChevronDown className="h-3 w-3 text-[#666]" />}
  </button>
);

const IconButton = ({ children, label, className = "" }) => (
  <button
    aria-label={label}
    className={`flex h-8 w-8 items-center justify-center rounded-full text-[#3b3b3b] hover:bg-[#f4f4f6] transition-colors ${className}`}
  >
    {children}
  </button>
);

const Divider = ({ className = "" }) => (
  <span className={`mx-1 h-5 w-px bg-[#e4e4e4] ${className}`} />
);

export default function TopBar({ onToggleSidebar }) {
  const { user, signOut } = useAuth();
  const { business, users } = useBusiness();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { storePath } = useStorePath();

  const storeName = business?.name || "My Store";
  const accountName = users[user?.uid]?.name || user?.displayName || user?.email || "";
  const initial = (accountName || "?").trim().charAt(0).toUpperCase();

  return (
    <header className="flex h-12 w-full items-center gap-1 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.12)] bg-white px-2 sm:px-4">
      {/* Hamburger (mobile / tablet) */}
      <button
        onClick={onToggleSidebar}
        aria-label="Toggle menu"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#3b3b3b] hover:bg-[#f4f4f6] transition-colors xl:hidden"
      >
        <IoMenuOutline className="h-[18px] w-[18px]" />
      </button>

      {/* Brand */}
      <Link to={storePath("home")} className="flex shrink-0 items-center pr-2">
        <img src={logo} alt="Stocko" className="h-[1.2em] w-auto sm:h-[1.35em]" />
      </Link>

      {/* Account switcher (hidden on small): full-height, framed by side borders */}
      <button className="hidden self-stretch items-center gap-1.5 border-x border-[#e4e4e4] bg-[#f4f8ff] px-3 hover:bg-[#e9f1ff] transition-colors md:flex">
        <span className="text-[13.5px] font-semibold text-[#111] whitespace-nowrap">{storeName}</span>
        <IoChevronDown className="h-3 w-3 text-[#666]" />
      </button>

      {/* Primary nav (desktop only) */}
      <nav className="hidden items-center gap-1 xl:flex">
        <NavLink hasCaret>Explore</NavLink>
        <NavLink>Hire a Professional</NavLink>
        <NavLink hasCaret>Help</NavLink>
      </nav>

      {/* Upgrade (hidden on the smallest screens) */}
      <button className="ml-1 hidden shrink-0 rounded-full bg-[#8a2be2] px-3.5 py-1 text-[13.5px] font-semibold text-white hover:bg-[#7a1fd0] transition-colors sm:inline-flex">
        Upgrade
      </button>

      {/* Search (flexible, always present) */}
      <div className="mx-1 flex min-w-0 flex-1 justify-center sm:mx-3">
        <div className="flex w-full max-w-[720px] min-w-0 items-center gap-2 rounded-full bg-[#eaf3ff] px-3 py-1 ring-1 ring-transparent focus-within:ring-[#c3ddff] transition sm:px-4">
          <IoSearchOutline className="h-3.5 w-3.5 shrink-0 text-[#5a6b85]" />
          <input
            type="text"
            placeholder="Search for tools, apps, help & more..."
            className="w-full min-w-0 bg-[#eaf3ff] text-[13.5px] text-[#333] placeholder:text-[#8090a8] outline-none"
          />
        </div>
      </div>

      {/* Right tray */}
      <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
        <Divider className="hidden sm:block" />

        <IconButton label="Notifications">
          <IoNotificationsOutline className="h-4 w-4" />
        </IconButton>

        {/* Avatar + account menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            className="ml-0.5 flex items-center gap-1 rounded-full p-0.5 hover:bg-[#f4f4f6] transition-colors sm:ml-1"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#6b46c1] text-[13.5px] font-semibold text-white">
              {initial}
            </span>
            <IoChevronDown className="hidden h-3 w-3 text-[#666] sm:block" />
          </button>

          {menuOpen && (
            <>
              {/* Click-away layer, behind the menu but above the page. */}
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-xl border border-[#e6e7ea] bg-white py-2 shadow-lg">
                <div className="border-b border-[#f0f0f2] px-4 pb-2">
                  <p className="truncate text-[13.5px] font-semibold text-[#111]">{accountName}</p>
                  <p className="truncate text-[12px] text-[#5a6272]">{storeName}</p>
                </div>
                <button
                  onClick={signOut}
                  className="mt-1 w-full px-4 py-2 text-left text-[13.5px] font-medium text-[#333] hover:bg-[#f4f4f6] transition-colors"
                >
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>

        <Divider className="hidden sm:block" />

        {/* AI button (icon only on mobile) */}
        <button className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-[#3b5bff] to-[#8a2be2] px-2.5 py-1 text-[13.5px] font-semibold text-white shadow-sm hover:opacity-90 transition-opacity sm:px-3">
          <IoSparkles className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">AI</span>
        </button>
      </div>
    </header>
  );
}
